import { isEmpty } from 'lodash';

import { AccountService } from '@sm360/widget-commons-general';
import { ShowroomService } from '@sm360/widget-commons-vehicle';
import { LeadSubmissionBuilder, LEAD_TYPES } from '@sm360/widget-commons-leads';

import TradeInVehicleMapperService from 'trade-in/services/TradeInVehicleMapperService/TradeInVehicleMapperService.js';

/**
 * If there's an authenticated user, save the tradeIn in the account (ShowroomAPI) otherwise just resolve
 * @param {TradeInModel} tradeIn
 * @returns {Promise} that returns the tradeIn ID
 */
function showroomSaveTradeIn(tradeIn) {
    return AccountService.isAuthenticated()
        ? ShowroomService.saveTradeInVehicle({
              id: tradeIn.id,
              vehicle: TradeInVehicleMapperService.tradeInVehicleToVehicle(tradeIn.tradeInVehicle),
              amount: tradeIn.estimation ? tradeIn.estimation.amount : undefined,
              evaluationQuestions: tradeIn.evaluationQuestions,
          }).then(({ id }) => id)
        : Promise.resolve(tradeIn.id);
}

/**
 * Service that handles the logic with the creation and update of the Trade In depending of the situation
 * (User logged or not, lead created or not, etc.)
 */
class TradeInService {
    /**
     * Saves the trade in in the logged account and create/update the lead with the trade in information
     * @param {TradeInModel} tradeIn
     * @returns {Promise} the returns the created/updated `TradeInModel`
     */
    saveTradeIn(tradeIn, quote) {
        const showroomServicePromise = showroomSaveTradeIn(tradeIn);

        let leadServicePromise;
        if (!isEmpty(tradeIn.estimation)) {
            leadServicePromise = this.leadSaveTradeInEstimation(tradeIn, quote);

            return Promise.all([showroomServicePromise, leadServicePromise]).then(([tradeInId]) => {
                return tradeIn.set('id', tradeInId);
            });
        }
        if (tradeIn.evaluationQuestions) {
            leadServicePromise = this.leadSaveTradeInEvaluation(tradeIn, quote);
            return Promise.all([showroomServicePromise, leadServicePromise]).then(([tradeInId]) => {
                return tradeIn.set('id', tradeInId);
            });
        }
        return null;
    }

    leadSaveTradeInEstimation(tradeIn, quote) {
        const isVehicleNew = quote && quote.vehicle && quote.vehicle.getIsVehicleNew();

        const { firstname, lastname, email, homePhone, phoneNumber } = tradeIn.profile;
        const { make, model, trim, style, year, odometer, exteriorColor, vin } = tradeIn.tradeInVehicle;

        const { amount, high, low } = tradeIn.estimation;
        if (AccountService.isAuthenticated()) {
            return new LeadSubmissionBuilder()
                .addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED)
                .addContactInformation(firstname, lastname, phoneNumber || homePhone, email)
                .addVehicle(quote)
                .addTradeInVehicle(
                    parseInt(year, 10),
                    make,
                    model,
                    trim,
                    style,
                    exteriorColor,
                    parseInt(odometer, 10),
                    vin
                )
                .addTradeInEstimation(parseInt(amount, 10), parseInt(low, 10), parseInt(high, 10))
                .submit();
        }
        return new LeadSubmissionBuilder()
            .addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED)
            .addContactInformation(firstname, lastname, phoneNumber || homePhone, email)
            .addTradeInVehicle(parseInt(year, 10), make, model, trim, style, exteriorColor, parseInt(odometer, 10), vin)
            .addTradeInEstimation(parseInt(amount, 10), parseInt(low, 10), parseInt(high, 10))
            .submit();
    }

    leadSaveTradeInEvaluation(tradeIn, quote) {
        const isVehicleNew = quote && quote.vehicle && quote.vehicle.getIsVehicleNew();

        const { firstname, lastname, email, homePhone, phoneNumber } = tradeIn.profile;
        const { make, model, trim, style, year, odometer, exteriorColor, vin } = tradeIn.tradeInVehicle;

        return new LeadSubmissionBuilder()
            .addLeadType(isVehicleNew ? LEAD_TYPES.TRADE_IN_NEW : LEAD_TYPES.TRADE_IN_USED)
            .addContactInformation(firstname, lastname, phoneNumber || homePhone, email)
            .addVehicle(quote)
            .addTradeInVehicle(parseInt(year, 10), make, model, trim, style, exteriorColor, parseInt(odometer, 10), vin)
            .addTradeInEvaluation(tradeIn.evaluationQuestions)
            .submit();
    }
}

export default new TradeInService();



// WEBPACK FOOTER //
// ./src/services/TradeInService/TradeInService.js